import { z } from 'zod';
import { useAuthStore } from '@/stores';

// Minimal user ref (accept any extra fields)
const UserRefSchema = z.object({ id: z.number() }).passthrough();

// Backend aligned schema (raw response)
export const BackendStudyOfferSchema = z.object({
  id: z.number(),
  study_id: z.number(),
  offered_by: z.union([z.number(), UserRefSchema]),
  accepted_by: z.union([z.number(), UserRefSchema]).nullable().optional(),
  region_id: z.number(),
  notes: z.string().nullable().optional(),
  status: z.enum(['pending', 'accepted', 'declined', 'expired', 'cancelled']),
  expires_at: z.string(),
  accepted_at: z.string().nullable().optional(),
  declined_at: z.string().nullable().optional(),
  cancelled_at: z.string().nullable().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  // when loaded with relations
  study: z
    .object({
      id: z.number(),
      contact_id: z.number().optional().nullable(),
      contact: z
        .object({
          id: z.number(),
          name: z.string().nullable().optional(),
          address_lat: z.number().nullable().optional(),
          address_lng: z.number().nullable().optional(),
        })
        .optional()
        .nullable(),
    })
    .optional()
    .nullable(),
});

export type BackendStudyOffer = z.infer<typeof BackendStudyOfferSchema>;

export interface Paginated<T> {
  data: T[];
  meta?: {
    total: number;
    current_page: number;
    per_page: number;
    last_page: number;
  };
}

export default class StudyOffersService {
  private static authHeaders() {
    try {
      const token = useAuthStore.getState().token;
      return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
      return {};
    }
  }
  private static async ensureCsrf(): Promise<string | null> {
    try {
      // Get CSRF cookie (sets XSRF-TOKEN cookie)
      await fetch('/sanctum/csrf-cookie', { credentials: 'include' });
      // Extract XSRF-TOKEN from cookies
      const m = document.cookie.match(/(?:^|; )XSRF-TOKEN=([^;]+)/);
      if (!m) return null;
      return decodeURIComponent(m[1]);
    } catch {
      return null;
    }
  }
  // List offers with optional geofilter and pagination
  static async list(params: {
    regionId: number;
    status?: 'pending' | 'accepted' | 'declined' | 'expired';
    page?: number;
    perPage?: number;
    lat?: number;
    lng?: number;
    radius?: number; // km
  }): Promise<Paginated<BackendStudyOffer>> {
    const url = new URL('/api/study/offers', window.location.origin);
    url.searchParams.set('region_id', String(params.regionId));
    if (params.status) url.searchParams.set('status', params.status);
    if (params.page) url.searchParams.set('page', String(params.page));
    if (params.perPage) url.searchParams.set('per_page', String(params.perPage));
    if (params.lat !== undefined) url.searchParams.set('lat', String(params.lat));
    if (params.lng !== undefined) url.searchParams.set('lng', String(params.lng));
    if (params.radius !== undefined) url.searchParams.set('radius', String(params.radius));

    const baseHeaders: Record<string, string> = { Accept: 'application/json' };
    const auth = this.authHeaders() as Record<string, string>;
    const headers: Record<string, string> = { ...baseHeaders, ...auth };
    const res = await fetch(url.toString(), {
      credentials: 'include',
      headers,
    });
    if (!res.ok) throw new Error('Failed to list study offers');
    const json = (await res.json()) as Paginated<unknown>;
    const parsed = (json.data || []).map((raw) => BackendStudyOfferSchema.parse(raw));
    return { data: parsed, meta: json.meta };
  }

  // Create a new offer for a given study schedule
  static async create(input: {
    studyId: number;
    expiresAt: string; // ISO string
    notes?: string;
  }): Promise<BackendStudyOffer> {
    const xsrf = await this.ensureCsrf();
    const baseHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
    if (xsrf) baseHeaders['X-XSRF-TOKEN'] = xsrf;
    const auth = this.authHeaders() as Record<string, string>;
    const headers: Record<string, string> = { ...baseHeaders, ...auth };
    const res = await fetch('/api/study/offers', {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        study_id: input.studyId,
        expires_at: input.expiresAt,
        notes: input.notes,
      }),
    });
    if (!res.ok) throw new Error('Failed to create study offer');
    const json = await res.json();
    return BackendStudyOfferSchema.parse(json);
  }

  // Get a specific offer by ID
  static async get(offerId: number): Promise<BackendStudyOffer> {
    const baseHeaders: Record<string, string> = { Accept: 'application/json' };
    const auth = this.authHeaders() as Record<string, string>;
    const headers: Record<string, string> = { ...baseHeaders, ...auth };
    const res = await fetch(`/api/study/offers/${offerId}`, {
      credentials: 'include',
      headers,
    });
    if (!res.ok) throw new Error('Failed to fetch study offer');
    const json = await res.json();
    return BackendStudyOfferSchema.parse(json);
  }

  // Accept an offer
  static async accept(offerId: number): Promise<{ success: true; data?: any; message?: string }> {
    const xsrf = await this.ensureCsrf();
    const baseHeaders: Record<string, string> = { Accept: 'application/json' };
    if (xsrf) baseHeaders['X-XSRF-TOKEN'] = xsrf;
    const auth = this.authHeaders() as Record<string, string>;
    const headers: Record<string, string> = { ...baseHeaders, ...auth };
    const res = await fetch(`/api/study/offers/${offerId}/accept`, {
      method: 'POST',
      credentials: 'include',
      headers,
    });
    if (!res.ok) {
      try {
        const data = await res.json();
        const msg = (data && typeof data.message === 'string') ? data.message : 'Falha ao aceitar a oferta';
        throw new Error(msg);
      } catch {
        throw new Error('Falha ao aceitar a oferta');
      }
    }
    try {
      const json = await res.json();
      return { success: true, data: json?.data, message: json?.message };
    } catch {
      return { success: true };
    }
  }

  // Decline an offer
  static async decline(offerId: number): Promise<{ success: true; message?: string }> {
    const xsrf = await this.ensureCsrf();
    const baseHeaders: Record<string, string> = { Accept: 'application/json' };
    if (xsrf) baseHeaders['X-XSRF-TOKEN'] = xsrf;
    const auth = this.authHeaders() as Record<string, string>;
    const headers: Record<string, string> = { ...baseHeaders, ...auth };
    const res = await fetch(`/api/study/offers/${offerId}/decline`, {
      method: 'POST',
      credentials: 'include',
      headers,
    });
    if (!res.ok) {
      try {
        const data = await res.json();
        const msg = (data && typeof data.message === 'string') ? data.message : 'Falha ao recusar a oferta';
        throw new Error(msg);
      } catch {
        throw new Error('Falha ao recusar a oferta');
      }
    }
    try {
      const json = await res.json();
      return { success: true, message: json?.message };
    } catch {
      return { success: true };
    }
  }
}
